import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Card,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
} from "@mantine/core";

import { EmptyMessage } from "@components/shared";

function personLabel(person, fallbackId) {
  if (!person) return fallbackId ? String(fallbackId) : "—";
  const name = `${person.first_name || ""} ${person.last_name || ""}`.trim();
  if (name && person.email) return `${name} (${person.email})`;
  return name || person.email || String(fallbackId || "—");
}

const ACTIVITY_STAMP_KEYS = {
  0: "assigned_at",
  1: "started_at",
  2: "submitted_at",
  3: "scored_at",
  4: "processed_at",
};

const FILTER_STAMP_KEYS = {
  assigned: "assigned_at",
  started: "started_at",
  submitted: "submitted_at",
  scored: "scored_at",
  processed: "processed_at",
};

function formatLocalStamp(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
}

function titleCaseStatus(value) {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function lastCompletedActivity(row) {
  const stampKey = ACTIVITY_STAMP_KEYS[row.activity_status];
  return {
    label: titleCaseStatus(row.activity_status_formatted),
    at: stampKey ? formatLocalStamp(row[stampKey]) : null,
  };
}

function activityStampLine(row, activityStatus) {
  if (activityStatus === "entire") {
    const last = lastCompletedActivity(row);
    if (!last.label && !last.at) return null;
    if (!last.at) return last.label;
    return `${last.label} · ${last.at}`;
  }

  const label = titleCaseStatus(activityStatus);
  const at = formatLocalStamp(row[FILTER_STAMP_KEYS[activityStatus]]);
  if (!at) return label || null;
  return `${label} · ${at}`;
}

export default function AssessmentList({
  organizationId = null,
  userId = null,
  refresh = false,
  linkPrefix = "/assessments",
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityStatus, setActivityStatus] = useState("entire");

  useEffect(() => {
    fetchData();
  }, [organizationId, userId, refresh, activityStatus]);

  function fetchData() {
    setLoading(true);
    const url = organizationId
      ? `/organizations/${organizationId}/retrieve-assessments/`
      : `/retrieve-assessments/`;
    const body = {
      page: 0,
      page_size: 100,
      activity_status: activityStatus,
    };
    if (organizationId) body.organization_id = organizationId;
    if (userId) body.user_id = userId;

    axios
      .post(url, body)
      .then(({ data }) => {
        setRows(data.response[0]?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="sm">
      <SegmentedControl
        value={activityStatus}
        onChange={setActivityStatus}
        data={[
          { label: t("entire"), value: "entire" },
          { label: t("assigned"), value: "assigned" },
          { label: t("started"), value: "started" },
          { label: t("submitted"), value: "submitted" },
          { label: t("scored"), value: "scored" },
          { label: t("processed"), value: "processed" },
        ]}
      />
      {!rows.length && <EmptyMessage />}
      {rows.map((row) => {
        const stampLine = activityStampLine(row, activityStatus);
        return (
        <Card
          key={row.id}
          component={Link}
          to={
            organizationId
              ? `/organizations/${organizationId}/assessments/${row.id}`
              : `${linkPrefix}/${row.id}`
          }
        >
          <Group justify="space-between" align="flex-start">
            <div>
              <Text c="dark" fw={700}>
                {row.name ||
                  row.assessment_instrument?.name ||
                  String(row.id)}
              </Text>
              <Text size="sm" c="dimmed">
                {personLabel(row.user, row.user_id)}
              </Text>
              {row.assessment_instrument?.name && (
                <Text size="sm" c="dimmed">
                  {row.assessment_instrument.name}
                  {row.assessment_instrument.variety_formatted
                    ? ` (${row.assessment_instrument.variety_formatted})`
                    : ""}
                </Text>
              )}
              {stampLine && (
                <Text size="sm" c="dimmed">
                  {stampLine}
                </Text>
              )}
            </div>
            <Badge variant="light">
              {row.activity_status_formatted || row.status_formatted}
            </Badge>
          </Group>
        </Card>
        );
      })}
    </Stack>
  );
}
